import type { Pwf } from "../api/pwf";
import type { RitualKey } from "../api/lifeOs";
import { localDateKey } from "./reflectionDrip";

// Ritual schedule logic shared by RitualCard, the rhythm line and For now
// (spec 2026-11-04 §3, §5). "Done today" is the person's LOCAL day: the server
// stamps `at` (ms) on every log entry; `date` is a UTC key and is only the
// fallback for entries written before `at` existed.

export type ScheduledRitual = "morning" | "evening" | "weekly";

export interface RitualPrefs {
  morning?: { enabled: boolean; time: string };
  evening?: { enabled: boolean; time: string };
  weekly?: { enabled: boolean; day: string; time: string };
}

export interface RitualLogEntry {
  date: string;
  at?: number;
  ritual: string;
  summary?: string;
  intention?: string;
}

export interface RitualDay {
  hasCompass: boolean;
  prefs: RitualPrefs | null;
  log: RitualLogEntry[];
}

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
export const MORNING_CUTOFF_MIN = 14 * 60;

export const minutesOf = (hhmm: string): number => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : -1;
};

/** Same heuristic as lib/cockpitState: first non-template, parentless pwf with config.cockpit. */
const compassOf = (pwfs: Pwf[]) =>
  pwfs.find((p) => !p.isTemplate && !p.pwfMeta?.fiParent && Boolean((p.config as any)?.cockpit)) ?? null;

export function readRituals(pwfs: Pwf[]): RitualDay {
  const compass = compassOf(pwfs ?? []);
  const cockpit = ((compass?.config as any)?.cockpit ?? {}) as Record<string, any>;
  return {
    hasCompass: Boolean(compass),
    prefs: (cockpit.rituals ?? null) as RitualPrefs | null,
    log: (Array.isArray(cockpit.ritualLog) ? cockpit.ritualLog : []) as RitualLogEntry[],
  };
}

export const entryLocalDay = (e: RitualLogEntry): string =>
  typeof e.at === "number" && Number.isFinite(e.at) ? localDateKey(new Date(e.at)) : String(e.date ?? "").slice(0, 10);

export const doneToday = (log: RitualLogEntry[], key: string, now: Date): boolean => {
  const today = localDateKey(now);
  return log.some((e) => e.ritual === key && entryLocalDay(e) === today);
};

/** Which ritual deserves the person's attention right now, if any. */
export const dueRitual = (prefs: RitualPrefs | null, log: RitualLogEntry[], now: Date): ScheduledRitual | null => {
  if (!prefs) return null;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const weekday = WEEKDAYS[now.getDay()];
  if (
    prefs.weekly?.enabled &&
    prefs.weekly.day === weekday &&
    nowMin >= minutesOf(prefs.weekly.time) &&
    !doneToday(log, "weekly", now)
  )
    return "weekly";
  if (
    prefs.morning?.enabled &&
    nowMin >= minutesOf(prefs.morning.time) &&
    nowMin < MORNING_CUTOFF_MIN &&
    !doneToday(log, "morning", now)
  )
    return "morning";
  if (prefs.evening?.enabled && nowMin >= minutesOf(prefs.evening.time) && !doneToday(log, "evening", now))
    return "evening";
  return null;
};

export type RhythmSlot = "morning" | "checkin" | "evening";

export interface RhythmChip {
  slot: RhythmSlot;
  ritual: RitualKey;
  state: "due" | "done" | "default";
}

/** Morning · Check in · Evening. A due weekly takes the label of its chip (before 14:00 → Morning). */
export function rhythmChips(prefs: RitualPrefs | null, log: RitualLogEntry[], now: Date): RhythmChip[] {
  const due = dueRitual(prefs, log, now);
  const weeklyMin = prefs?.weekly ? minutesOf(prefs.weekly.time) : -1;
  const weeklySlot: "morning" | "evening" = weeklyMin >= 0 && weeklyMin < MORNING_CUTOFF_MIN ? "morning" : "evening";
  const scheduled = (slot: "morning" | "evening"): RhythmChip => {
    if (due === "weekly" && weeklySlot === slot) return { slot, ritual: "weekly", state: "due" };
    const state = due === slot ? "due" : doneToday(log, slot, now) ? "done" : "default";
    return { slot, ritual: slot, state };
  };
  return [scheduled("morning"), { slot: "checkin", ritual: "checkin", state: "default" }, scheduled("evening")];
}
